"use client";

import { useState, useEffect, useRef, Suspense, lazy } from "react";
import { createPortal } from "react-dom";
import Link from "next/link";
import { usePublicPathname } from "@/lib/i18n/navigation";
import {
  m as motion,
  AnimatePresence,
  useMotionValue,
  useSpring,
  useScroll,
} from "framer-motion";
import { PhoneCall, ChevronDown } from "lucide-react";
import { useTranslations, useLocale } from "next-intl";
import { Button } from "@/components/ui/button";
import { useScrollToTop } from "@/hooks/useScrollToTop";
import { usePageVisibility } from "@/hooks/usePageVisibility";
import { localizedRoutes } from "@/lib/routes/routes";
import { trackEvent } from "@/utils/analytics";
import {
  MEGA_SERVICES,
  projectEntries,
  toolEntries,
  DEMO_ENTRY,
} from "./megaMenuData";

// Voci di nav che aprono un mega menu (desktop) / accordion (mobile).
const MEGA_KEYS = new Set<string>(["services", "projects"]);

const ThemeToggle = lazy(() =>
  import("@/components/ThemeToggle").then((mod) => ({ default: mod.ThemeToggle }))
);
const LanguageSwitch = lazy(() =>
  import("@/components/LanguageSwitch").then((mod) => ({
    default: mod.LanguageSwitch,
  }))
);
const ServicesMegaPanel = lazy(() =>
  import("./ServicesMegaPanel").then((mod) => ({
    default: mod.ServicesMegaPanel,
  }))
);
const ProjectsMegaPanel = lazy(() =>
  import("./ProjectsMegaPanel").then((mod) => ({
    default: mod.ProjectsMegaPanel,
  }))
);

type MenuPreview = {
  src: string;
  title: { it: string; en: string };
  description: { it: string; en: string };
  label: { it: string; en: string };
  imageClassName?: string;
};

function createMenuPreviewSvg({
  badge,
  titleLines,
  accent,
  eyebrow,
}: {
  badge: string;
  titleLines: [string, string];
  accent: string;
  eyebrow: string;
}) {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 720 720" width="720" height="720">
      <defs>
        <linearGradient id="bg" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="#020814"/>
          <stop offset="55%" stop-color="#04111f"/>
          <stop offset="100%" stop-color="#07192a"/>
        </linearGradient>
        <radialGradient id="glow" cx="50%" cy="48%" r="58%">
          <stop offset="0%" stop-color="rgba(33,224,235,0.24)"/>
          <stop offset="100%" stop-color="rgba(33,224,235,0)"/>
        </radialGradient>
      </defs>
      <rect width="720" height="720" rx="40" fill="url(#bg)"/>
      <rect x="34" y="34" width="652" height="652" rx="32" fill="rgba(2,8,20,0.22)" stroke="rgba(69,214,222,0.16)"/>
      <circle cx="560" cy="174" r="180" fill="url(#glow)"/>
      <rect x="82" y="98" width="170" height="42" rx="21" fill="rgba(5,18,27,0.9)" stroke="rgba(69,214,222,0.34)"/>
      <circle cx="108" cy="119" r="7" fill="#2ce6b5"/>
      <text x="126" y="126" fill="#d4faff" font-size="18" font-family="JetBrains Mono, monospace">${badge}</text>
      <text x="82" y="196" fill="#21e0eb" font-size="19" font-family="JetBrains Mono, monospace" letter-spacing="4">${eyebrow}</text>
      <text x="82" y="286" fill="#ecf4fb" font-size="58" font-weight="700" font-family="Space Grotesk, Arial, sans-serif">${titleLines[0]}</text>
      <text x="82" y="356" fill="#ecf4fb" font-size="58" font-weight="700" font-family="Space Grotesk, Arial, sans-serif">${titleLines[1]}</text>
      <text x="82" y="452" fill="#21e0eb" font-size="34" font-weight="700" font-family="Space Grotesk, Arial, sans-serif">${accent}</text>
      <rect x="82" y="544" width="272" height="56" rx="18" fill="#21e0eb"/>
      <text x="118" y="579" fill="#03131b" font-size="24" font-weight="700" font-family="Space Grotesk, Arial, sans-serif">Prenota una call</text>
      <rect x="374" y="544" width="202" height="56" rx="18" fill="transparent" stroke="rgba(255,255,255,0.18)"/>
      <text x="425" y="579" fill="#ecf4fb" font-size="24" font-weight="700" font-family="Space Grotesk, Arial, sans-serif">Scopri</text>
    </svg>
  `;

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

const menuPreviewMap: Record<string, MenuPreview> = {
  home: {
    src: createMenuPreviewSvg({
      badge: "Studio Creativo",
      titleLines: ["Siti web e MVP", "che convertono"],
      accent: "misurabili.",
      eyebrow: "HOME",
    }),
    title: {
      it: "Dimensione 4",
      en: "Dimension 4",
    },
    description: {
      it: "Sviluppo siti e MVP che portano risultati misurabili.",
      en: "Websites and MVPs built for measurable results.",
    },
    label: { it: "Home", en: "Home" },
    imageClassName: "object-cover",
  },
  about: {
    src: createMenuPreviewSvg({
      badge: "Chi sono",
      titleLines: ["Dario Marco", "Bellini"],
      accent: "Lavori con me, direttamente.",
      eyebrow: "ABOUT",
    }),
    title: {
      it: "Chi sono",
      en: "About",
    },
    description: {
      it: "Direzione tecnica, design e sviluppo in un unico flusso.",
      en: "Technical direction, design, and development in one flow.",
    },
    label: { it: "Chi sono", en: "About" },
  },
  services: {
    src: createMenuPreviewSvg({
      badge: "Servizi",
      titleLines: ["Interventi chiari per", "risultati misurabili"],
      accent: "WordPress, MVP, frontend.",
      eyebrow: "SERVIZI",
    }),
    title: {
      it: "Servizi",
      en: "Services",
    },
    description: {
      it: "Interventi chiari per risultati misurabili e più conversione.",
      en: "Clear interventions for measurable outcomes and more conversions.",
    },
    label: { it: "Servizi", en: "Services" },
  },
  mvp: {
    src: createMenuPreviewSvg({
      badge: "MVP Custom",
      titleLines: ["Dal concept al", "lancio"],
      accent: "in settimane, non mesi.",
      eyebrow: "MVP",
    }),
    title: {
      it: "MVP",
      en: "MVP",
    },
    description: {
      it: "Dal concept al lancio in settimane, non mesi.",
      en: "From concept to launch in weeks, not months.",
    },
    label: { it: "MVP", en: "MVP" },
  },
  projects: {
    src: createMenuPreviewSvg({
      badge: "Progetti",
      titleLines: ["Build reali,", "non slide"],
      accent: "Progetti veri, in crescita.",
      eyebrow: "PROGETTI",
    }),
    title: {
      it: "Progetti",
      en: "Projects",
    },
    description: {
      it: "Siti, web app e prodotti reali: WordPress, WooCommerce e web app.",
      en: "Real websites, web apps and products: WordPress, WooCommerce, and web apps.",
    },
    label: { it: "Progetti", en: "Projects" },
  },
  blog: {
    src: createMenuPreviewSvg({
      badge: "Blog",
      titleLines: ["Guide, consigli", "e casi reali"],
      accent: "Sviluppo web, AI, strumenti.",
      eyebrow: "BLOG",
    }),
    title: { it: "Blog", en: "Blog" },
    description: {
      it: "Guide e consigli pratici su sviluppo web, AI e gli strumenti che uso.",
      en: "Practical guides and tips on web development, AI and the tools I use.",
    },
    label: { it: "Blog", en: "Blog" },
  },
  subscription: {
    src: createMenuPreviewSvg({
      badge: "Abbonamento",
      titleLines: ["Supporto continuativo", "ogni settimana"],
      accent: "Fix, sviluppo, revisione.",
      eyebrow: "ABBONAMENTO",
    }),
    title: {
      it: "Abbonamento",
      en: "Subscription",
    },
    description: {
      it: "Supporto continuativo per sviluppo, fix e miglioramenti prioritari.",
      en: "Ongoing support for development, fixes, and priority improvements.",
    },
    label: { it: "Abbonamento", en: "Subscription" },
  },
  contacts: {
    src: createMenuPreviewSvg({
      badge: "Contatti",
      titleLines: ["Prenota una call", "di 30 minuti"],
      accent: "Definiamo il prossimo step.",
      eyebrow: "CONTATTI",
    }),
    title: {
      it: "Contatti",
      en: "Contact",
    },
    description: {
      it: "Prenota una call e definiamo il prossimo step operativo.",
      en: "Book a call and define the next concrete step.",
    },
    label: { it: "Contatti", en: "Contact" },
  },
};

// Magnetic link component
function MagneticLink({
  href,
  isActive,
  prefetch,
  index,
  previewKey,
  onEnter,
  children,
}: {
  href: string;
  isActive: boolean;
  prefetch?: boolean;
  index: number;
  previewKey: string;
  onEnter?: (index: number, previewKey: string) => void;
  children: React.ReactNode;
}) {
  const ref = useRef<HTMLAnchorElement>(null);
  // Cache the bounding rect on enter / resize to avoid forced reflows
  // when reading layout inside onMouseMove on every pointer event.
  const rectRef = useRef<{
    centerX: number;
    centerY: number;
  } | null>(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const { scrollToTop } = useScrollToTop();

  const springConfig = { damping: 15, stiffness: 150 };
  const springX = useSpring(x, springConfig);
  const springY = useSpring(y, springConfig);

  const measure = () => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    rectRef.current = {
      centerX: rect.left + rect.width / 2,
      centerY: rect.top + rect.height / 2,
    };
  };

  const handleMouseEnter = () => {
    onEnter?.(index, previewKey);
    measure();
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    const cached = rectRef.current;
    if (!cached) {
      // First move without a prior enter (rare) → measure once, then use cache.
      measure();
      return;
    }

    // Magnetic pull strength
    const pullStrength = 0.3;
    x.set((e.clientX - cached.centerX) * pullStrength);
    y.set((e.clientY - cached.centerY) * pullStrength);
  };

  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
    rectRef.current = null;
  };

  // Invalidate cached rect on window resize / scroll so we never use stale
  // coordinates. We cannot use ResizeObserver alone here because window
  // scroll also shifts the absolute rect position.
  useEffect(() => {
    const invalidate = () => {
      rectRef.current = null;
    };
    window.addEventListener("resize", invalidate, { passive: true });
    window.addEventListener("scroll", invalidate, { passive: true });
    return () => {
      window.removeEventListener("resize", invalidate);
      window.removeEventListener("scroll", invalidate);
    };
  }, []);

  const handleClick = () => {
    // Scroll to top when clicking navigation links
    scrollToTop();
  };

  return (
    <Link
      ref={ref}
      href={href}
      prefetch={prefetch}
      onMouseEnter={handleMouseEnter}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
      data-navlink=""
      data-index={index}
      className="relative px-2.5 min-[1400px]:px-3 min-[1650px]:px-4 py-2 text-[0.92rem] min-[1650px]:text-sm font-medium whitespace-nowrap"
    >
      {isActive && (
        <motion.div
          layoutId="nav-pill"
          className="absolute inset-0 bg-primary/10 rounded-full"
          transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
        />
      )}
      <motion.span
        style={{ x: springX, y: springY }}
        className={`relative z-10 block transition-colors duration-300 ${
          isActive
            ? "text-primary"
            : "text-muted-foreground hover:text-primary"
        }`}
      >
        {children}
      </motion.span>
    </Link>
  );
}

export function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const [isTabletViewport, setIsTabletViewport] = useState(false);
  const [visualViewportTop, setVisualViewportTop] = useState(0);
  const [headerDynamicReady, setHeaderDynamicReady] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [forceOffsetTopZeroForHome, setForceOffsetTopZeroForHome] =
    useState(false);
  // Desktop nav: hovered link drives the cyan link color + the preview panel,
  // whose center is read from linkCenters (measured via ResizeObserver) so the
  // panel travels with transform only - no per-hover layout reads.
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const [hoverPreviewKey, setHoverPreviewKey] = useState<string | null>(null);
  const [megaOpen, setMegaOpen] = useState(false);
  // Mobile: quale voce mega e' espansa nell'accordion (services | projects | null).
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const navPillRef = useRef<HTMLDivElement>(null);
  const [linkCenters, setLinkCenters] = useState<number[]>([]);
  const pathname = usePublicPathname();
  const t = useTranslations();
  const locale = useLocale();
  const { scrollToTop } = useScrollToTop();
  const { isVisible } = usePageVisibility();
  const currentLang: "it" | "en" = locale === "en" ? "en" : "it";
  const routes = localizedRoutes[currentLang];
  const isHomeRoute = pathname === routes.home;

  useEffect(() => {
    setMounted(true);
    setForceOffsetTopZeroForHome(
      new URLSearchParams(window.location.search).get("d4ForceOffsetTop0") ===
        "1"
    );
  }, []);
  const bookingLink = `${routes.contacts}#tidycal`;
  // Base localizzata degli strumenti (le pagine vivono sotto app/[locale]/strumenti).
  const strumentiBase = currentLang === "en" ? "/en/strumenti" : "/strumenti";
  const navLinks = [
    { href: routes.home, labelKey: "nav.home" },
    { href: routes.about, labelKey: "nav.about", key: "about" as const },
    {
      href: routes.services,
      labelKey: "nav.services",
      key: "services" as const,
    },
    { href: routes.mvp, labelKey: "nav.mvp", key: "mvp" as const },
    {
      href: routes.projects,
      labelKey: "nav.projects",
      key: "projects" as const,
    },
    {
      href: routes.subscription,
      labelKey: "nav.subscription",
      key: "subscription" as const,
    },
    { href: routes.blog, labelKey: "nav.blog", key: "blog" as const },
    {
      href: routes.contacts,
      labelKey: "nav.contacts",
      key: "contacts" as const,
    },
  ].filter(
    (link) =>
      !("key" in link) ||
      !link.key ||
      link.key === "projects" ||
      isVisible(link.key),
  );
  const menuPreviewLinks = [
    { href: routes.home, previewKey: "home" },
    { href: routes.about, previewKey: "about" },
    { href: routes.services, previewKey: "services" },
    { href: routes.mvp, previewKey: "mvp" },
    { href: routes.projects, previewKey: "projects" },
    { href: routes.subscription, previewKey: "subscription" },
    { href: routes.blog, previewKey: "blog" },
    { href: routes.contacts, previewKey: "contacts" },
  ] as const;
  const activePreviewKey =
    menuPreviewLinks.find((item) => item.href === pathname)?.previewKey ??
    "home";
  const [previewKey, setPreviewKey] =
    useState<keyof typeof menuPreviewMap>(activePreviewKey);
  const previewContent = menuPreviewMap[previewKey];
  const isDesktopViewport = !isMobileViewport && !isTabletViewport;

  // Page scroll progress for the indicator
  const { scrollYProgress } = useScroll();

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    let timeoutId: number | null = null;
    let idleId: number | null = null;
    const requestIdle = window.requestIdleCallback?.bind(window);
    const cancelIdle = window.cancelIdleCallback?.bind(window);

    const enableDynamicHeader = () => setHeaderDynamicReady(true);

    if (requestIdle) {
      idleId = requestIdle(enableDynamicHeader, {
        timeout: 900,
      });
    } else {
      timeoutId = window.setTimeout(enableDynamicHeader, 500);
    }

    const interactionEvents: Array<keyof WindowEventMap> = [
      "scroll",
      "touchstart",
      "pointerdown",
      "keydown",
    ];

    const onInteraction = () => {
      setHeaderDynamicReady(true);
      interactionEvents.forEach((eventName) =>
        window.removeEventListener(eventName, onInteraction)
      );
    };

    interactionEvents.forEach((eventName) => {
      window.addEventListener(eventName, onInteraction, {
        passive: true,
        once: true,
      });
    });

    return () => {
      if (idleId !== null && cancelIdle) {
        cancelIdle(idleId);
      }
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
      interactionEvents.forEach((eventName) =>
        window.removeEventListener(eventName, onInteraction)
      );
    };
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 767px)");
    const handleChange = () => setIsMobileViewport(mediaQuery.matches);
    handleChange();
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia(
      "(min-width: 768px) and (max-width: 1279px)"
    );
    const handleChange = () => setIsTabletViewport(mediaQuery.matches);
    handleChange();
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  useEffect(() => {
    const updateViewportTop = () => {
      const nextTop = Math.max(0, window.visualViewport?.offsetTop ?? 0);
      setVisualViewportTop(nextTop);
    };

    updateViewportTop();
    window.addEventListener("resize", updateViewportTop);
    window.visualViewport?.addEventListener("resize", updateViewportTop);
    window.visualViewport?.addEventListener("scroll", updateViewportTop);

    return () => {
      window.removeEventListener("resize", updateViewportTop);
      window.visualViewport?.removeEventListener("resize", updateViewportTop);
      window.visualViewport?.removeEventListener("scroll", updateViewportTop);
    };
  }, []);

  const scrollToCalendly = () => {
    const target =
      document.getElementById("tidycal-desktop") ??
      document.getElementById("tidycal-mobile");
    if (!target) return;

    const headerOffset = window.innerWidth >= 768 ? 96 : 84;
    const top =
      target.getBoundingClientRect().top + window.scrollY - headerOffset;
    window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
  };

  const handleBookingLinkClick = (
    event: React.MouseEvent<HTMLAnchorElement>,
    location: "header" | "header_mobile_menu" = "header"
  ) => {
    trackEvent("book_call_click", {
      tool: "tidycal",
      location,
    });

    setIsMenuOpen(false);

    if (pathname === routes.contacts) {
      event.preventDefault();
      if (window.location.hash !== "#tidycal") {
        window.history.replaceState(null, "", `${routes.contacts}#tidycal`);
      }
      window.setTimeout(scrollToCalendly, 40);
    }
  };

  const toggleMenu = () => {
    setIsMenuOpen((prev) => !prev);
  };

  const handleMenuToggle = (event?: React.SyntheticEvent) => {
    event?.preventDefault();
    event?.stopPropagation();
    toggleMenu();
  };

  const handleLogoClick = () => {
    setIsMenuOpen(false);
    scrollToTop();
  };

  // Close menu on route change
  useEffect(() => {
    setIsMenuOpen(false);
    setMegaOpen(false);
    setMobileExpanded(null);
  }, [pathname]);

  useEffect(() => {
    setPreviewKey(activePreviewKey);
  }, [activePreviewKey]);

  // Measure nav link centers (relative to the pill) and re-measure on any size
  // change: resize, font swap, language change, visibility filter. Move stays
  // transform-only - no per-hover layout reads.
  useEffect(() => {
    const el = navPillRef.current;
    if (!el) return;
    const measure = () => {
      const nodes = el.querySelectorAll<HTMLElement>("[data-navlink]");
      setLinkCenters(
        Array.from(nodes).map((n) => n.offsetLeft + n.offsetWidth / 2)
      );
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [navLinks.length, locale]);

  // Force-close mobile menu when switching to desktop breakpoints.
  useEffect(() => {
    const mediaQuery = window.matchMedia("(min-width: 1280px)");
    const handleChange = (event: MediaQueryListEvent) => {
      if (event.matches) {
        setIsMenuOpen(false);
      }
    };

    mediaQuery.addEventListener("change", handleChange);

    if (mediaQuery.matches) {
      setIsMenuOpen(false);
    }

    return () => {
      mediaQuery.removeEventListener("change", handleChange);
    };
  }, []);

  // Lock body scroll when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isMenuOpen]);

  // Keep subtitle visible when user is back near the top area.
  // A slightly higher threshold avoids "sticky hidden" behavior with snap scrolling.
  const viewportTopOffset =
    headerDynamicReady && isMobileViewport
      ? isHomeRoute && forceOffsetTopZeroForHome
        ? 0
        : Math.round(visualViewportTop)
      : 0;
  const effectiveScrollY = scrollY + viewportTopOffset;
  const hasScrolled = headerDynamicReady && effectiveScrollY > 90;
  const headerHeight = isMobileViewport ? 80 : hasScrolled ? 72 : 92;
  const mobileMenuTopOffset = `calc(${headerHeight}px + env(safe-area-inset-top, 0px) + 8px + ${viewportTopOffset}px)`;

  // Hover preview anchors to the hovered link's center (measured into
  // linkCenters relative to the pill). Travel is transform-only via framer `x`.
  const previewCenter =
    hoverIndex != null ? linkCenters[hoverIndex] : undefined;
  const hoverPreview = menuPreviewMap[hoverPreviewKey ?? ""];

  const mobileMenu = (
    <AnimatePresence>
      {isMenuOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          id="mobile-navigation"
          className="xl:hidden fixed inset-0 bg-background/98 supports-[backdrop-filter]:bg-background/86 supports-[backdrop-filter]:backdrop-blur-xl z-[10010] overflow-y-auto pointer-events-auto"
          style={{
            paddingTop: mobileMenuTopOffset,
            WebkitOverflowScrolling: "touch",
          }}
        >
          <motion.nav
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.2 }}
            className="container-fluid py-4 pb-6 flex flex-col min-h-full"
          >
            <div className="flex flex-1 flex-col gap-6 px-2 sm:px-3 md:grid md:grid-cols-[minmax(0,0.86fr)_minmax(320px,0.84fr)] md:items-start md:gap-8 md:px-4">
              <div className="flex flex-col gap-0.5">
                {navLinks.map((link, index) => {
                  const itemPreviewKey =
                    menuPreviewLinks.find((item) => item.href === link.href)
                      ?.previewKey ?? "home";

                  const megaKey =
                    "key" in link && link.key != null && MEGA_KEYS.has(link.key)
                      ? link.key
                      : null;
                  const isExpanded = megaKey != null && mobileExpanded === megaKey;

                  // Sotto-voci dell'accordion (riuso dei dati del mega desktop).
                  const subLinks: {
                    href: string;
                    label: string;
                    inProgress?: boolean;
                  }[] =
                    megaKey === "services"
                      ? [
                          ...MEGA_SERVICES.map((s) => ({
                            href: `${localizedRoutes.it.services}/${s.slug}`,
                            label: s.title[currentLang],
                          })),
                          {
                            href: routes.services,
                            label:
                              currentLang === "it"
                                ? "Tutti i servizi"
                                : "All services",
                          },
                        ]
                      : megaKey === "projects"
                        ? [
                            ...projectEntries(routes.projects).map((p) => ({
                              href: p.href,
                              label: p.title[currentLang],
                            })),
                            {
                              href: "",
                              label: DEMO_ENTRY.title[currentLang],
                              inProgress: true,
                            },
                            ...toolEntries(strumentiBase).map((toolItem) => ({
                              href: toolItem.href,
                              label: toolItem.title[currentLang],
                            })),
                          ]
                        : [];

                  return (
                    <motion.div
                      key={link.href}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.03 }}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <Link
                          href={link.href}
                          prefetch={link.href === routes.contacts ? false : undefined}
                          onMouseEnter={() => {
                            if (isTabletViewport) {
                              setPreviewKey(itemPreviewKey);
                            }
                          }}
                          onFocus={() => {
                            if (isTabletViewport) {
                              setPreviewKey(itemPreviewKey);
                            }
                          }}
                          onClick={() => {
                            setIsMenuOpen(false);
                            scrollToTop();
                          }}
                          className={`block flex-1 py-2.5 md:py-3 text-[clamp(1.7rem,5vw,2.15rem)] md:text-[clamp(2rem,4.4vw,2.55rem)] font-display font-semibold leading-none transition-colors duration-300 ${
                            pathname === link.href
                              ? "text-primary"
                              : "text-muted-foreground hover:text-foreground"
                          }`}
                        >
                          <span className="inline-block">{t(link.labelKey)}</span>
                          {pathname === link.href && (
                            <motion.span
                              layoutId="mobile-indicator"
                              className="inline-block w-2 h-2 bg-primary rounded-full ml-3 align-middle"
                            />
                          )}
                        </Link>

                        {megaKey ? (
                          <button
                            type="button"
                            onClick={() =>
                              setMobileExpanded((prev) =>
                                prev === megaKey ? null : megaKey
                              )
                            }
                            aria-expanded={isExpanded}
                            aria-label={`${
                              currentLang === "it"
                                ? isExpanded
                                  ? "Comprimi"
                                  : "Espandi"
                                : isExpanded
                                  ? "Collapse"
                                  : "Expand"
                            } ${t(link.labelKey)}`}
                            className="shrink-0 rounded-full border border-[hsl(var(--border))] bg-surface/50 p-2 text-muted-foreground transition-colors hover:text-foreground"
                          >
                            <ChevronDown
                              className={`h-5 w-5 transition-transform duration-300 ${
                                isExpanded ? "rotate-180" : ""
                              }`}
                              aria-hidden="true"
                            />
                          </button>
                        ) : null}
                      </div>

                      <AnimatePresence initial={false}>
                        {megaKey && isExpanded ? (
                          <motion.ul
                            initial={{ opacity: 0, y: -6 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -6 }}
                            transition={{
                              duration: 0.2,
                              ease: [0.16, 1, 0.3, 1],
                            }}
                            className="mb-1 ml-1 border-l border-[hsl(var(--border))] pl-4"
                          >
                            {subLinks.map((sl) =>
                              sl.inProgress ? (
                                <li key={`${megaKey}-${sl.label}`}>
                                  <span className="flex items-center gap-2 py-2 text-lg text-muted-foreground/60">
                                    {sl.label}
                                    <span className="rounded-full border border-[hsl(var(--border))] px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wide">
                                      In progress
                                    </span>
                                  </span>
                                </li>
                              ) : (
                                <li key={`${megaKey}-${sl.href}`}>
                                  <Link
                                    href={sl.href}
                                    onClick={() => {
                                      setIsMenuOpen(false);
                                      scrollToTop();
                                    }}
                                    className="block py-2 text-lg text-muted-foreground transition-colors hover:text-foreground"
                                  >
                                    {sl.label}
                                  </Link>
                                </li>
                              )
                            )}
                          </motion.ul>
                        ) : null}
                      </AnimatePresence>
                    </motion.div>
                  );
                })}
              </div>

              {isTabletViewport ? (
                <motion.aside
                  initial={{ opacity: 0, x: 12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.14, duration: 0.22 }}
                  className="hidden md:block w-full"
                >
                  <div className="overflow-hidden rounded-[28px] border border-primary/20 bg-surface/70 supports-[backdrop-filter]:backdrop-blur-xl shadow-[0_20px_50px_rgba(0,0,0,0.35)]">
                    <div className="relative aspect-[0.88] overflow-hidden border-b border-[hsl(var(--border))] bg-[radial-gradient(circle_at_center,hsl(var(--primary)/0.16),transparent_68%)]">
                      <img
                        src={previewContent.src}
                        alt=""
                        aria-hidden="true"
                        loading="eager"
                        decoding="async"
                        className={`h-full w-full transition-transform duration-500 ${
                          previewContent.imageClassName ?? "object-cover"
                        }`}
                      />
                      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-background via-background/18 to-transparent" />
                    </div>
                    <div className="space-y-3 p-5 md:p-6">
                      <p className="text-[11px] font-mono uppercase tracking-[0.28em] text-primary/90">
                        {previewContent.label[currentLang]}
                      </p>
                      <div className="space-y-2">
                        <h3 className="font-display text-[1.65rem] leading-tight text-foreground">
                          {previewContent.title[currentLang]}
                        </h3>
                        <p className="text-[0.95rem] leading-relaxed text-muted-foreground">
                          {previewContent.description[currentLang]}
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.aside>
              ) : null}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="mt-4 px-2 pt-5 border-t border-[hsl(var(--border))] space-y-4 sm:px-3 md:px-4"
            >
              <div className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,0.9fr)_minmax(0,0.9fr)_minmax(0,1.2fr)]">
                <div className="relative overflow-hidden rounded-[26px] border border-primary/15 bg-gradient-to-br from-surface/95 via-surface/75 to-background/80 p-4 md:p-4 text-left shadow-[0_16px_40px_rgba(0,0,0,0.22)]">
                  <span className="absolute right-4 top-4 h-3 w-3 rounded-full bg-primary shadow-[0_0_18px_rgba(33,224,235,0.75)]" />
                  <div className="flex items-start justify-between md:hidden max-w-[15rem]">
                    <div className="text-left">
                      <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-primary/80">
                        {currentLang === "it" ? "Lingua" : "Language"}
                      </p>
                      <div className="mt-2 flex items-center justify-start">
                        <Suspense
                          fallback={
                            <div
                              className="h-11 min-w-[110px] rounded-[16px] border border-primary/20 bg-background/45"
                              aria-hidden="true"
                            />
                          }
                        >
                          <LanguageSwitch className="h-11 w-auto min-w-[110px] justify-center rounded-[16px] border-primary/20 bg-background/45 px-3 py-2.5 text-sm shadow-none" />
                        </Suspense>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-primary/80">
                        {currentLang === "it" ? "Tema" : "Theme"}
                      </p>
                      <div className="mt-2 flex items-center justify-end">
                        <Suspense
                          fallback={
                            <div
                              className="h-11 w-11 rounded-[16px] border border-primary/20 bg-background/45"
                              aria-hidden="true"
                            />
                          }
                        >
                          <ThemeToggle className="h-11 w-11 rounded-[16px] border-primary/20 bg-background/45 [&_svg]:h-5 [&_svg]:w-5" />
                        </Suspense>
                      </div>
                    </div>
                  </div>

                  <div className="hidden md:block">
                    <div className="mb-3 flex items-center justify-start">
                      <div className="text-left max-w-[10rem]">
                        <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-primary/85">
                          {currentLang === "it" ? "Lingua" : "Language"}
                        </p>
                        <p className="mt-1 text-base md:text-[1.08rem] font-medium text-foreground">
                          {t("common.language")}
                        </p>
                      </div>
                    </div>
                    <Suspense
                      fallback={
                        <div
                          className="mx-auto h-12 md:h-[3.15rem] min-w-[122px] rounded-[18px] border border-primary/20 bg-background/45"
                          aria-hidden="true"
                        />
                      }
                    >
                      <LanguageSwitch className="mx-auto h-12 md:h-[3.15rem] w-auto min-w-[122px] justify-center rounded-[18px] border-primary/20 bg-background/45 px-3.5 py-3 text-base md:text-[1.05rem] shadow-none" />
                    </Suspense>
                  </div>
                </div>

                <div className="relative hidden overflow-hidden rounded-[26px] border border-primary/15 bg-gradient-to-br from-surface/95 via-surface/75 to-background/80 p-4 text-left shadow-[0_16px_40px_rgba(0,0,0,0.22)] md:block md:p-4">
                  <span className="absolute right-4 top-4 h-3 w-3 rounded-full bg-primary/75 shadow-[0_0_18px_rgba(33,224,235,0.55)]" />
                  <div className="mb-4 flex items-center justify-start">
                    <div className="text-left">
                      <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-primary/85">
                        {currentLang === "it" ? "Atmosfera" : "Atmosphere"}
                      </p>
                      <p className="mt-1 text-base md:text-[1.08rem] font-medium text-foreground">
                        {t("common.theme")}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center justify-center">
                    <Suspense
                      fallback={
                        <div
                          className="h-12 w-12 md:h-[3.35rem] md:w-[3.35rem] rounded-[18px] border border-primary/20 bg-background/45"
                          aria-hidden="true"
                        />
                      }
                    >
                      <ThemeToggle className="h-12 w-12 md:h-[3.35rem] md:w-[3.35rem] rounded-[18px] border-primary/20 bg-background/45 [&_svg]:h-5 [&_svg]:w-5 md:[&_svg]:h-6 md:[&_svg]:w-6" />
                    </Suspense>
                  </div>
                </div>

                <div className="relative overflow-hidden rounded-[26px] border border-primary/15 bg-gradient-to-br from-surface/95 via-surface/75 to-background/80 p-4 md:p-4 text-left shadow-[0_16px_40px_rgba(0,0,0,0.22)]">
                  <span className="absolute right-4 top-4 h-3 w-3 rounded-full bg-primary shadow-[0_0_18px_rgba(33,224,235,0.75)]" />
                  <div className="relative flex max-w-md flex-col items-start">
                    <div className="mb-1 flex items-center justify-start">
                      <p className="text-[10px] font-mono uppercase tracking-[0.24em] text-primary/90">
                        {currentLang === "it"
                          ? "Disponibilità"
                          : "Availability"}
                      </p>
                    </div>
                    <p className="text-base md:text-[1.08rem] font-medium text-foreground">
                      {currentLang === "it"
                        ? "Parliamo del prossimo step"
                        : "Let's discuss the next step"}
                    </p>
                    <p className="mt-2 text-xs md:text-[0.8rem] leading-relaxed text-muted-foreground">
                      {currentLang === "it"
                        ? "Call breve, priorità chiare e prossima mossa definita."
                        : "A short call to align priorities and define the next move."}
                    </p>
                  </div>

                  <Button
                    variant="hero"
                    size="default"
                    className="mt-3 w-full justify-center rounded-[16px] text-sm md:text-[0.9rem] min-h-11"
                    asChild
                  >
                    <Link
                      href={bookingLink}
                      prefetch={false}
                      onClick={(event) => handleBookingLinkClick(event, "header_mobile_menu")}
                    >
                      {currentLang === "it" ? "Prenota call" : "Book call"}
                      <PhoneCall className="w-5 h-5 ml-2" />
                    </Link>
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.nav>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-[10020] isolate ${
          headerDynamicReady ? "transition-all duration-500" : ""
        } ${
          isMenuOpen
            ? "bg-background"
            : hasScrolled
              ? "bg-background/60 backdrop-blur-2xl"
              : "bg-transparent"
        }`}
        style={{
          top: `${viewportTopOffset}px`,
          paddingTop: "env(safe-area-inset-top, 0px)",
        }}
      >
        {/* Scroll Progress Indicator */}
        <motion.div
          className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-primary via-accent to-primary origin-left"
          style={{ scaleX: scrollYProgress }}
        />

        <div className="container-wide relative z-[10021] pointer-events-auto">
          {/* <motion.div
          className="flex items-center justify-between"
          animate={{
            height: hasScrolled ? 64 : 80,
          }}
          transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
        > */}
          <motion.div
            className="flex items-center w-full"
            animate={{ height: headerHeight }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
          >
            {/* Left side - Logo */}
            <div className="flex items-center max-xl:pl-1">
              <Link
                href={routes.home}
                onClick={handleLogoClick}
                className="flex items-center gap-3 group relative z-10"
                style={{ minHeight: 40 }}
              >
                <motion.div
                  whileHover={{ rotate: 180 }}
                  transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                  animate={{
                    scale: isMobileViewport ? 1 : hasScrolled ? 0.9 : 1,
                  }}
                  style={{ width: 40, height: 40 }}
                  className="shrink-0"
                >
                  {/*
                    Single-source image (96.webp) matches the document-level
                    <link rel="preload" imageSrcSet> in app/[locale]/page.tsx,
                    so the browser reuses the preloaded resource instead of
                    fetching logo-symbol-64.webp and warning that the preload
                    went unused. fetchPriority="high" is React's camelCase
                    attribute → forwarded as `fetchpriority` to the DOM.
                  */}
                  <img
                    src="/brand/logo-symbol-96.webp"
                    alt=""
                    aria-hidden="true"
                    fetchPriority="high"
                    decoding="async"
                    width={40}
                    height={40}
                    style={{ width: 40, height: 40 }}
                    className="object-contain"
                  />
                </motion.div>
                <div
                  className="min-w-0 flex flex-col items-start justify-center"
                  style={{ minHeight: 40 }}
                >
                  <span className="font-display font-semibold text-lg tracking-tight whitespace-nowrap leading-tight">
                    Dimensione 4
                  </span>
                  {/* Ragione sociale sotto il logo: sempre visibile, in ciano
                      (anche da scrollato). Reso sempre -> nessun CLS. */}
                  <span className="text-[11px] font-medium text-primary whitespace-nowrap leading-none mt-0.5">
                    {currentLang === "it"
                      ? "di Dario Marco Bellini"
                      : "by Dario Marco Bellini"}
                  </span>
                </div>
              </Link>
            </div>

            {/* Center - Desktop Navigation (hidden on mobile/tablet) */}
            <div className="hidden xl:flex flex-1 justify-center min-w-0 px-3 min-[1650px]:px-5">
              <nav className="flex justify-center min-w-0">
                {/*
                  Pill uses static gap/padding to avoid animating non-composite
                  layout properties (padding/gap) which trigger reflows and
                  Lighthouse "non-composited animations" warnings. The subtle
                  shrink-on-scroll effect is achieved via a transform: scale
                  applied to the whole pill, which stays on the compositor.
                */}
                <motion.div
                  ref={navPillRef}
                  onMouseLeave={() => {
                    setHoverIndex(null);
                    setHoverPreviewKey(null);
                    setMegaOpen(false);
                  }}
                  style={{
                    maxWidth: "100%",
                    width: "fit-content",
                    transformOrigin: "center",
                  }}
                  className="relative flex items-center gap-0.5 rounded-full bg-surface/50 backdrop-blur-xl border border-[hsl(var(--border))] px-2 py-1.5"
                  animate={{ scale: hasScrolled ? 0.96 : 1 }}
                  transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                >
                  {navLinks.map((link, i) => {
                    const previewKey =
                      menuPreviewLinks.find((m) => m.href === link.href)
                        ?.previewKey ?? "home";
                    return (
                      <MagneticLink
                        key={link.href}
                        href={link.href}
                        isActive={pathname === link.href}
                        prefetch={link.href === routes.contacts ? false : undefined}
                        index={i}
                        previewKey={previewKey}
                        onEnter={(idx, pk) => {
                          setHoverIndex(idx);
                          setHoverPreviewKey(pk);
                          setMegaOpen(MEGA_KEYS.has(pk));
                        }}
                      >
                        {t(link.labelKey)}
                      </MagneticLink>
                    );
                  })}

                  {/* Hover preview drop-panel - anchored under the hovered link.
                      `x` is the link center (transform-only travel); marginLeft
                      pulls it back by half its width so it stays centered. */}
                  <AnimatePresence>
                    {hoverPreview &&
                      hoverPreviewKey != null &&
                      !MEGA_KEYS.has(hoverPreviewKey) && (
                      <motion.div
                        key="nav-preview"
                        initial={{ opacity: 0, y: 8, x: previewCenter ?? 0 }}
                        animate={{ opacity: 1, y: 0, x: previewCenter ?? 0 }}
                        exit={{ opacity: 0, y: 8 }}
                        transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
                        className="pointer-events-none absolute left-0 top-full mt-3 w-[360px] -ml-[180px]"
                      >
                        <div className="flex items-center gap-4 overflow-hidden rounded-2xl border border-primary/20 bg-surface/85 p-3 backdrop-blur-xl shadow-[0_24px_60px_rgba(0,0,0,0.45)]">
                          <div className="relative h-[4.5rem] w-[4.5rem] shrink-0 overflow-hidden rounded-xl border border-[hsl(var(--border))]">
                            <img
                              src={hoverPreview.src}
                              alt=""
                              aria-hidden="true"
                              loading="eager"
                              decoding="async"
                              className={`h-full w-full ${
                                hoverPreview.imageClassName ?? "object-cover"
                              }`}
                            />
                          </div>
                          <div className="min-w-0">
                            <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-primary/90">
                              {hoverPreview.label[currentLang]}
                            </p>
                            <h3 className="mt-1 font-display text-lg leading-tight text-foreground">
                              {hoverPreview.title[currentLang]}
                            </h3>
                            <p className="mt-1 line-clamp-2 text-[0.82rem] leading-snug text-muted-foreground">
                              {hoverPreview.description[currentLang]}
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Mega menu (Servizi / Progetti): card-preview centrato sotto la nav */}
                  <AnimatePresence>
                    {megaOpen && (
                      <motion.div
                        key="nav-mega"
                        initial={{ opacity: 0, y: 8, x: "-50%" }}
                        animate={{ opacity: 1, y: 0, x: "-50%" }}
                        exit={{ opacity: 0, y: 8, x: "-50%" }}
                        transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                        className="pointer-events-auto absolute left-1/2 top-full z-50 w-[min(880px,calc(100vw-2rem))] pt-3"
                      >
                        <Suspense fallback={null}>
                          {hoverPreviewKey === "projects" ? (
                            <ProjectsMegaPanel
                              lang={currentLang}
                              projectsBase={routes.projects}
                              strumentiBase={strumentiBase}
                              onNavigate={() => {
                                setMegaOpen(false);
                                scrollToTop();
                              }}
                            />
                          ) : (
                            <ServicesMegaPanel
                              lang={currentLang}
                              servicesBase={routes.services}
                              onNavigate={() => {
                                setMegaOpen(false);
                                scrollToTop();
                              }}
                            />
                          )}
                        </Suspense>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              </nav>
            </div>

            {/* Right side - Desktop controls */}
            {isDesktopViewport ? (
              <div className="ml-auto hidden xl:flex items-center gap-2 min-[1400px]:gap-3 relative z-10">
                <Suspense
                  fallback={<div className="h-10 w-[112px]" aria-hidden="true" />}
                >
                  <LanguageSwitch />
                </Suspense>
                <div className="hidden min-[1240px]:block">
                  <Suspense fallback={<div className="h-10 w-10" aria-hidden="true" />}>
                    <ThemeToggle />
                  </Suspense>
                </div>
                <motion.div
                  animate={{ scale: hasScrolled ? 0.95 : 1 }}
                  transition={{ duration: 0.3 }}
                >
                  <Button
                    variant="hero"
                    size={hasScrolled ? "sm" : "default"}
                    className="group max-[1450px]:px-4"
                    asChild
                  >
                    <Link
                      href={bookingLink}
                      prefetch={false}
                      onClick={(event) => handleBookingLinkClick(event, "header")}
                    >
                      {t("common.bookCall")}
                      <PhoneCall className="w-4 h-4 ml-2" />
                    </Link>
                  </Button>
                </motion.div>
              </div>
            ) : null}

            {/* Mobile Hamburger Menu */}
            <button
              type="button"
              onClick={handleMenuToggle}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  handleMenuToggle(event);
                }
              }}
              className="xl:hidden ml-auto max-xl:mr-1 relative w-10 h-10 flex items-center justify-center rounded-full bg-surface/50 backdrop-blur-xl border border-[hsl(var(--border))] z-[10030] touch-manipulation pointer-events-auto"
              aria-label="Toggle menu"
              aria-expanded={isMenuOpen}
              aria-controls="mobile-navigation"
            >
              {/*
                Hamburger lines driven by pure CSS transitions on composite
                properties (transform + opacity) - no framer-motion. Avoids the
                "unsupported timing parameters" warning that Lighthouse raises
                for runtime-driven motion values.
              */}
              <div className="relative w-5 h-5">
                <span
                  aria-hidden="true"
                  className="absolute left-0 top-1/2 block h-0.5 w-5 bg-foreground origin-center will-change-transform"
                  style={{
                    transform: isMenuOpen
                      ? "translateY(0px) rotate(45deg)"
                      : "translateY(-5px) rotate(0deg)",
                    transition: "transform 220ms cubic-bezier(0.4, 0, 0.2, 1)",
                  }}
                />
                <span
                  aria-hidden="true"
                  className="absolute left-0 top-1/2 block h-0.5 w-5 bg-foreground origin-center will-change-transform"
                  style={{
                    transform: isMenuOpen ? "scaleX(0)" : "scaleX(1)",
                    opacity: isMenuOpen ? 0 : 1,
                    transition:
                      "transform 180ms cubic-bezier(0.4, 0, 0.2, 1), opacity 180ms cubic-bezier(0.4, 0, 0.2, 1)",
                  }}
                />
                <span
                  aria-hidden="true"
                  className="absolute left-0 top-1/2 block h-0.5 w-5 bg-foreground origin-center will-change-transform"
                  style={{
                    transform: isMenuOpen
                      ? "translateY(0px) rotate(-45deg)"
                      : "translateY(5px) rotate(0deg)",
                    transition: "transform 220ms cubic-bezier(0.4, 0, 0.2, 1)",
                  }}
                />
              </div>
            </button>
          </motion.div>
        </div>
      </header>
      {mounted ? createPortal(mobileMenu, document.body) : null}
    </>
  );
}
